<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="gb-whatsapp-web" />

        <div class="markdown-content">
            <h1>GB WhatsApp Web – Features, Access & Tips</h1>

            <p>Access GB WhatsApp from your browser with WhatsApp Web. This comprehensive guide covers how to use web-based WhatsApp and maximize your experience across devices.</p>

            <h2>Introduction to GB WhatsApp Web</h2>

            <p>WhatsApp Web allows you to access WhatsApp from your computer's browser, providing a seamless experience across devices. While GB WhatsApp's web version is limited, this guide covers all available options.</p>

            <h2>How to Access GB WhatsApp Web</h2>

            <p>Steps to access WhatsApp Web:</p>
            <ol>
                <li><strong>Open Browser:</strong> Launch your preferred web browser</li>
                <li><strong>Navigate to Web:</strong> Go to WhatsApp Web website</li>
                <li><strong>Open Mobile App:</strong> Open WhatsApp on your phone</li>
                <li><strong>Scan QR Code:</strong> Scan the QR code with your phone</li>
                <li><strong>Stay Connected:</strong> Keep your phone's internet active</li>
                <li><strong>Access Chats:</strong> Start using WhatsApp on your computer</li>
            </ol>

            <h2>Browser Compatibility</h2>

            <p>Supported browsers and versions:</p>
            <ul>
                <li><strong>Chrome:</strong> Latest version recommended</li>
                <li><strong>Firefox:</strong> Latest version supported</li>
                <li><strong>Safari:</strong> Safari 14+ recommended</li>
                <li><strong>Edge:</strong> Chromium-based Edge supported</li>
                <li><strong>Opera:</strong> Opera browser supported</li>
            </ul>

            <h2>Features Supported on Web</h2>

            <p>What you can do on WhatsApp Web:</p>
            <ul>
                <li><strong>Send Messages:</strong> Type and send text messages</li>
                <li><strong>Media Sharing:</strong> Send and receive images and videos</li>
                <li><strong>Voice Messages:</strong> Record and send voice messages</li>
                <li><strong>File Transfer:</strong> Share documents and files</li>
                <li><strong>Status Updates:</strong> View and post status updates</li>
            </ul>

            <h2>Limitations Compared to Mobile</h2>

            <p>Known restrictions on web version:</p>
            <ul>
                <li><strong>GB WhatsApp Features:</strong> Limited GB WhatsApp-specific features</li>
                <li><strong>Theme Customization:</strong> Basic theme options only</li>
                <li><strong>Privacy Controls:</strong> Reduced privacy options</li>
                <li><strong>No Direct Installation:</strong> Cannot install GB WhatsApp directly</li>
                <li><strong>Feature Updates:</strong> May lag behind mobile app</li>
            </ul>

            <h2>Media Sharing on Web</h2>

            <p>Media capabilities on web:</p>
            <ul>
                <li><strong>Image Sharing:</strong> Drag and drop or click to upload</li>
                <li><strong>Video Sharing:</strong> Share videos directly from computer</li>
                <li><strong>Document Sharing:</strong> Send PDF, Word, and other files</li>
                <li><strong>Download Media:</strong> Save received media to computer</li>
                <li><strong>Quality Settings:</strong> Options for image compression</li>
            </ul>

            <h2>Multi-Account Usage on Web</h2>

            <p>Managing multiple accounts:</p>
            <ul>
                <li><strong>Multiple Sessions:</strong> Use multiple WhatsApp accounts</li>
                <li><strong>Profile Switching:</strong> Switch between profiles easily</li>
                <li><strong>Separate Windows:</strong> Open multiple windows for different accounts</li>
                <li><strong>Notification Management:</strong> Centralized notification control</li>
                <li><strong>Session Management:</strong> Monitor active sessions</li>
            </ul>

            <h2>Security Considerations</h2>

            <p>Stay secure while using WhatsApp Web:</p>
            <ul>
                <li><strong>Public Computers:</strong> Avoid logging in on public devices</li>
                <li><strong>Session Timeout:</strong> Web sessions may expire</li>
                <li><strong>Logout Procedures:</strong> Always log out when finished</li>
                <li><strong>Two-Factor Auth:</strong> Enable two-step verification</li>
                <li><strong>Active Sessions:</strong> Regularly check active sessions</li>
            </ul>

            <h2>Troubleshooting Common Issues</h2>

            <p>Solve frequent problems:</p>
            <ul>
                <li><strong>QR Code Not Scanning:</strong> Ensure phone camera is working</li>
                <li><strong>Connection Lost:</strong> Check phone's internet connection</li>
                <li><strong>Messages Not Sending:</strong> Refresh page and retry</li>
                <li><strong>Media Not Loading:</strong> Check browser cache and internet</li>
                <li><strong>Session Expired:</strong> Re-scan QR code to reconnect</li>
            </ul>

            <h2>Tips for Smooth Web Experience</h2>

            <p>Maximize your web experience:</p>
            <ul>
                <li><strong>Use Latest Browser:</strong> Keep your browser updated</li>
                <li><strong>Stable Internet:</strong> Ensure both phone and PC have stable internet</li>
                <li><strong>Clear Cache:</strong> Regularly clear browser cache</li>
                <li><strong>Bookmark Page:</strong> Save WhatsApp Web as bookmark</li>
                <li><strong>Keyboard Shortcuts:</strong> Learn keyboard shortcuts for efficiency</li>
            </ul>

            <h2>Related Topics</h2>

            <p>Explore more related guides:</p>
            <ul>
                <li><a href="/features/media/">Media Sharing Features</a> - Learn about media sharing</li>
                <li><a href="/tutorials/installation/">Installation Guide</a> - General installation information</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'GbWhatsappWeb',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}
</style>
